import { useLocation } from 'react-router-dom';
import { useMargaret } from '@koob/margaret';
import {
  KoobOrganizationRequiredPrompt,
  useKoobSidebarCollapsed,
} from '@koob/ui';
import {
  ORGANIZATION_REQUIRED_EVENT,
  switchActingOrganization,
} from '@koob/utils';
import { useTranslation } from 'react-i18next';
import {
  closeTripWizard,
  useTripWizardMode,
} from '@koob/ui/src/features/tripWizard';
import { DesignTripWizard } from '@koob/trip-designer';
import MainSidebar from 'components/MainSidebar';
import TopBar from 'components/TopBar';
import { useDeepCompareEffect } from 'react-use';
import { useApp, useSearchParams } from 'hooks';

const Layout = ({ children }) => {
  const { key, pathname } = useLocation();
  const { collapseMainNav } = useMargaret();
  const [{ state }] = useSearchParams();
  const [sidebarCollapsed] = useKoobSidebarCollapsed();
  const wizardMode = useTripWizardMode();
  const { user } = useApp();
  const { t } = useTranslation();

  // The trip export page is a print-ready document (Save-as-PDF prints the
  // live DOM): the sidebar wrapper's ml-64 + `relative` would shift the whole
  // PDF content right by the sidebar width (KD-3326). The TO export page is
  // already standalone; align the DMC one.
  // The booking steps take the whole screen; the hotel search before them
  // keeps the sidebar.
  const shouldHideSidebar = Boolean(
    pathname.match(/^\/bookings\/new(?!\/hotel\b)/) ||
    pathname.match(/^\/trips\/[^/]+\/export/) ||
    // OAuth consent screen: standalone card, straight back to the MCP client.
    pathname.match(/^\/mcp\/authorize/),
  );

  useDeepCompareEffect(() => {
    collapseMainNav();
  }, [{ key }]);

  return (
    <>
      {!shouldHideSidebar && <MainSidebar />}

      <div
        className={[
          'relative min-h-screen transition-all duration-300',
          !shouldHideSidebar ? (sidebarCollapsed ? 'ml-16' : 'ml-72') : '',
          state === 'archived' ? 'bg-gray-200' : 'bg-gray-50',
        ].join(' ')}
      >
        {!shouldHideSidebar && <TopBar />}
        <main>{children}</main>
      </div>

      {/* One prompt for the app: a 400 ORGANIZATION_REQUIRED asks which
          organization the action is for and switches the picker (KD-3735). */}
      <KoobOrganizationRequiredPrompt
        organizations={user?.organizations ?? []}
        eventName={ORGANIZATION_REQUIRED_EVENT}
        onChoose={switchActingOrganization}
        title={t('misc:organizationRequiredTitle')}
        text={t('misc:organizationRequiredText')}
      />

      {/* One instance for the whole app: every entry point calls
          openTripWizard() and it lands over whatever page is showing. */}
      {wizardMode && (
        <DesignTripWizard
          mode={wizardMode}
          onDone={closeTripWizard}
          onExit={closeTripWizard}
        />
      )}
    </>
  );
};

export default Layout;
